import { Col, Form, type FormInstance, Input, Row, Switch, Tag, Typography } from 'antd';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import { Container } from '@/components/Container';
import { InputNumber } from '@/components/input/InputNumber';
import { useEmpresaQuery } from '@/features/empresa/hooks/useEmpresaQuery';
import { ProdutoTipoFiscalSelect } from '@/features/produto/components/ProdutoTipoFiscalSelect';
import { ProdutoUnidadeSelect } from '@/features/produto/components/ProdutoUnidadeSelect';
import { SelectProdutoCest } from '@/features/produto/components/SelectProdutoCest';
import { SelectProdutoNcm } from '@/features/produto/components/SelectProdutoNcm';
import { SelectProdutoOrigem } from '@/features/produto/components/SelectProdutoOrigem';
import { produtoNcmColors, produtoNcmText } from '@/features/produto/const';
import { SelectRegraFiscal } from '@/features/regra-fiscal/components/SelectRegraFiscal';
import { RegraFiscalTipo } from '@/features/regra-fiscal/types/RegraFiscal';

const { Link } = Typography;

function RenderNcmStatus({ status }: { status: string }) {
    const color = produtoNcmColors[status];

    return (
        <Tag color={color} variant='solid'>
            {produtoNcmText[status]?.toUpperCase()}
        </Tag>
    );
}

export function ProdutoTabFiscal({ form }: { form: FormInstance<FormProdutoValues> }) {
    const { empresa } = useEmpresaQuery();
    const isEmpresaSimplesNacional = empresa?.crt?.crt_crt === 1;

    const ncmStatus = Form.useWatch('pro_ncm_status', form);
    const proFiscalUtrib = Form.useWatch('pro_fiscal_utrib', form);
    const proFiscalQtrib = Form.useWatch('pro_fiscal_qtrib', form);
    const proPrecoVenda = Form.useWatch('pro_preco_venda', form);
    const ncmDescricao = Form.useWatch('pro_ncm_descricao', form);

    const vUnTrib =
        proFiscalQtrib && proPrecoVenda
            ? proFiscalQtrib
                ? proFiscalQtrib * proPrecoVenda
                : (proPrecoVenda ?? 0)
            : 0;

    return (
        <div className='flex flex-col gap-4'>
            <Container>
                <Row gutter={8}>
                    <Col md={8} sm={12} xs={24}>
                        <Form.Item
                            className='mb-3'
                            help='Obrigatório para emissão fiscal'
                            label='Tipo tributável'
                            name='pro_produto_tipo_codigo'
                        >
                            <ProdutoTipoFiscalSelect
                                onChange={(e) => {
                                    form.setFieldsValue({
                                        pro_produto_tipo_codigo: e?.prt_codigo || null,
                                    });
                                }}
                            />
                        </Form.Item>
                    </Col>

                    <Col md={8} sm={12} xs={24}>
                        <Form.Item
                            className='mb-3'
                            help='Obrigatório para emissão fiscal'
                            label={
                                <div>NCM {ncmStatus && <RenderNcmStatus status={ncmStatus} />}</div>
                            }
                            name='pro_ncm'
                            tooltip='Nomeclatura Comum do Mercosul - Código que identifica o produto nos países do Mercosul e para Receita.'
                        >
                            <SelectProdutoNcm
                                ncmDescription={ncmDescricao}
                                onChange={(ncm, description, status) => {
                                    form.setFieldsValue({
                                        pro_ncm: ncm,
                                        pro_ncm_descricao: description,
                                        pro_ncm_status: status,
                                    });
                                }}
                            />
                        </Form.Item>
                    </Col>

                    <Form.Item hidden={true} name='pro_ncm_descricao' noStyle={true}>
                        <Input />
                    </Form.Item>

                    <Form.Item hidden={true} name='pro_ncm_status' noStyle={true}>
                        <Input />
                    </Form.Item>

                    <Col md={8} sm={12} xs={24}>
                        <Form.Item
                            className='mb-3'
                            help={
                                <>
                                    Obrigatório para emissão fiscal em alguns produtos
                                    <Link
                                        href='https://www.confaz.fazenda.gov.br/legislacao/convenios/2015/CV092_15'
                                        rel='noreferrer noopener'
                                        style={{
                                            marginLeft: 5,
                                            textDecoration: 'underline',
                                        }}
                                        target='_blank'
                                    >
                                        legislação
                                    </Link>
                                </>
                            }
                            label='CEST'
                            name='pro_produto_cest_codigo'
                            rules={[{ min: 7, type: 'string' }]}
                            tooltip='Código Especificador da Substituição Tributária - Deve ser informado para produtos com incidência de ICMS Substituição Tributária'
                        >
                            <SelectProdutoCest
                                description={form.getFieldValue('pro_produto_cest_descricao')}
                                onChange={(cest) =>
                                    form.setFieldsValue({
                                        pro_produto_cest_codigo: cest?.prc_codigo,
                                        pro_produto_cest_descricao: cest?.prc_descricao_cest,
                                    })
                                }
                            />
                        </Form.Item>

                        <Form.Item hidden={true} name='pro_produto_cest_descricao' />
                    </Col>

                    <Col md={8} sm={12} xs={24}>
                        <Form.Item
                            className='mb-3'
                            help='Obrigatório para emissão fiscal'
                            label='Origem'
                            name='pro_produto_origem_codigo'
                        >
                            <SelectProdutoOrigem
                                onChange={(e) =>
                                    form.setFieldsValue({
                                        pro_produto_origem_codigo: e?.pro_codigo,
                                    })
                                }
                            />
                        </Form.Item>
                    </Col>

                    <Col md={8} sm={12} xs={24}>
                        <Form.Item
                            className='mb-3'
                            label='Quantidade de volumes'
                            name='pro_volume_qtde'
                            tooltip='Este campo indica a quantidade de volumes para cada unidade que for vendida deste produto'
                        >
                            <InputNumber />
                        </Form.Item>
                    </Col>

                    <Col md={4} sm={6} xs={12}>
                        <Form.Item className='mb-3' label='Peso Bruto (kg)' name='pro_peso_bruto'>
                            <InputNumber decimalScale={3} />
                        </Form.Item>
                    </Col>

                    <Col md={4} sm={6} xs={12}>
                        <Form.Item
                            className='mb-3'
                            label='Peso Líquido (kg)'
                            name='pro_peso_liquido'
                        >
                            <InputNumber decimalScale={3} />
                        </Form.Item>
                    </Col>

                    <Col md={8} sm={12} xs={24}>
                        <Form.Item
                            className='mb-3'
                            help='Obrigatório para emissão fiscal. Caso configurado uma regra padrão ela será preenchida ao tentar apagar o grupo.'
                            label='Grupo de regras fiscais'
                            name='pro_produto_regra_fiscal_idpk'
                            tooltip="Informe o grupo de regras fiscais para facilitar o preenchimento dos impostos na NF-e. Cadastro via menu 'Fiscal->Regras fiscais'"
                        >
                            <SelectRegraFiscal tipo={RegraFiscalTipo.Produto} />
                        </Form.Item>
                    </Col>

                    <Col md={8} sm={12} xs={24}>
                        <Form.Item
                            className='mb-3'
                            label='Código benefício fiscal'
                            name='pro_cbenef'
                            tooltip={
                                <span>
                                    Preencher apenas quando o produto possuir benefício fiscal com
                                    código específico. Alguns estados acitam a descrição &quot;SEM
                                    CBENEF&quot;
                                </span>
                            }
                        >
                            <Input placeholder='' />
                        </Form.Item>
                    </Col>

                    <Col md={8} sm={12} xs={24}>
                        <Form.Item
                            className='mb-3'
                            label='Informações adicionais'
                            name='pro_nota_fiscal_infadic'
                        >
                            <Input placeholder='Digite as informações aqui' />
                        </Form.Item>
                    </Col>

                    {!isEmpresaSimplesNacional && (
                        <Col md={8} sm={12} xs={24}>
                            <Form.Item
                                getValueFromEvent={(e) => {
                                    return e ? 1 : 0;
                                }}
                                getValueProps={(e) => {
                                    return {
                                        checked: e === 1,
                                    };
                                }}
                                label='Gerar SPED Fiscal'
                                name='pro_gerar_sped'
                                style={{ marginTop: 10 }}
                                valuePropName='checked'
                            >
                                <Switch checkedChildren='Sim' unCheckedChildren='Não' />
                            </Form.Item>
                        </Col>
                    )}
                </Row>
            </Container>

            <Container
                title={
                    <Container.Title
                        help='Se não informados estes campos, o sistema preencherá automaticamente conforme os dados gerais do produto.'
                        title='Opcional - Dados tributários do produto'
                    />
                }
            >
                <Row className='p-3' gutter={16}>
                    <Col lg={8} md={12} sm={12} xs={24}>
                        <Form.Item
                            label='Código de barras tributável (cEanTrib)'
                            name='pro_fiscal_ceantrib'
                        >
                            <Input placeholder='' />
                        </Form.Item>
                    </Col>
                    <Col lg={8} md={12} sm={12} xs={24}>
                        <Form.Item hidden name='pro_fiscal_utrib'>
                            <Input />
                        </Form.Item>

                        <Form.Item
                            label='Unidade tributável (uTrib)'
                            name='pro_fiscal_utrib_select'
                        >
                            <ProdutoUnidadeSelect
                                onChange={(_, unidade) => {
                                    form.setFieldsValue({
                                        pro_fiscal_utrib: unidade?.pru_sigla ?? null,
                                    });
                                }}
                            />
                        </Form.Item>
                    </Col>

                    <Col lg={8} md={12} sm={12} xs={24}>
                        <Form.Item
                            label='Fração da quantidade tributável (qTrib)'
                            name='pro_fiscal_qtrib'
                            tooltip={
                                <div style={{ maxWidth: 320 }}>
                                    <p>
                                        Divida a unidade principal deste produto pela quantidade de
                                        itens que o compõem.
                                    </p>
                                    <p className='mt-2 font-bold'>Exemplo</p>
                                    <p>
                                        1 Caixa de bombom com 50 unidades, divida a caixa pela
                                        quantidade de unidades e o resultado preencha neste campo.
                                    </p>
                                    <p>
                                        <strong>1 / 50 = 0,02</strong>
                                    </p>
                                </div>
                            }
                        >
                            <InputNumber decimalScale={2} />
                        </Form.Item>
                    </Col>

                    <Col lg={8} md={12} sm={12} xs={24}>
                        <Form.Item
                            initialValue={vUnTrib}
                            label='Valor unitário de tributação (vUnTrib)'
                            tooltip={
                                proFiscalUtrib !== undefined && proFiscalUtrib !== null
                                    ? `Campo calculado automaticamente do valor de cada ${proFiscalUtrib}.`
                                    : ''
                            }
                        >
                            <InputNumber
                                decimalScale={2}
                                disabled={true}
                                type='$'
                                value={vUnTrib}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Container>

            <Container
                title={
                    <Container.Title
                        help='Preenchimento destes campos devem ser baseados na nota fiscal de compra quando esta contiver ICMS ST.
                            Considerar custo pela mínima unidade de medida.
                            Procure orientações com seu contador.'
                        title='ICMS Substituição Tributária Retido Anteriormente'
                    />
                }
            >
                <Row className='p-3' gutter={16}>
                    <Col lg={8} md={12} sm={12} xs={24}>
                        <Form.Item label='Base de cálculo do ICMS ST' name='pro_icmsstret_base'>
                            <InputNumber decimalScale={2} type='$' />
                        </Form.Item>
                    </Col>

                    <Col lg={8} md={12} sm={12} xs={24}>
                        <Form.Item
                            label='Percentual de aliquota do ICMS ST'
                            name='pro_icmsstret_aliquota'
                        >
                            <InputNumber decimalScale={4} type='%' />
                        </Form.Item>
                    </Col>

                    <Col lg={8} md={12} sm={12} xs={24}>
                        <Form.Item label='Valor do ICMS ST' name='pro_icmsstret_valor'>
                            <InputNumber decimalScale={2} type='$' />
                        </Form.Item>
                    </Col>

                    <Col lg={8} md={12} sm={12} xs={24}>
                        <Form.Item
                            label='Valor do ICMS próprio do Substituto'
                            name='pro_icmsstret_substituto'
                        >
                            <InputNumber decimalScale={2} type='$' />
                        </Form.Item>
                    </Col>

                    <Col lg={8} md={12} sm={12} xs={24}>
                        <Form.Item
                            label='Percentual de FCP do ICMS ST'
                            name='pro_icmsstret_fcp_aliquota'
                        >
                            <InputNumber decimalScale={4} type='%' />
                        </Form.Item>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}
